import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnInit,
	Output,
} from '@angular/core';

type PlazoOption = {
	val: Number;
	active: Boolean;
};

@Component({
	selector: 'cmv-plazo-selector',
	templateUrl: './plazo-selector.component.html',
	styleUrls: ['./plazo-selector.component.scss'],
})
export class PlazoSelectorComponent implements OnInit, OnChanges {
	@Output() plazoChange = new EventEmitter<Number>();

	/** Muestra u oculta la pregunta "¿A cuántos meses quieres pagarlo?". */
	@Input() showTitle: boolean = true;
	@Input() valInput: Number = 36;
	/**
	 * Plazo máximo autorizado para el socio (el que regresa la oferta del motor).
	 * Los plazos mayores se ocultan. Con 0 o null se muestran todos.
	 */
	@Input() maxTerm: number = 0;
	@Input() plazos: Array<PlazoOption> = [
		{ val: 6, active: false },
		{ val: 12, active: false },
		{ val: 18, active: false },
		{ val: 24, active: false },
		{ val: 36, active: true },
	];

	/** Plazos que realmente se pintan, ya filtrados por el plazo máximo. */
	visiblePlazos: Array<PlazoOption> = [];

	private lastEmittedPlazo: number = null;

	constructor() {}

	ngOnInit(): void {
		// Si el componente se usa sin inputs enlazados ngOnChanges nunca corre.
		if (this.lastEmittedPlazo === null) {
			this.refreshPlazos();
		}
	}

	ngOnChanges(): void {
		this.refreshPlazos();
	}

	changePlazo(index: number): void {
		const selectedPlazo = this.visiblePlazos[index];
		if (!selectedPlazo) {
			return;
		}
		this.plazos.forEach(plazo => {
			plazo.active = plazo.val === selectedPlazo.val;
		});
		this.valInput = selectedPlazo.val;
		this.emitPlazo(Number(selectedPlazo.val));
	}

	private refreshPlazos(): void {
		const maxTerm = Number(this.maxTerm);
		const hasMaxTerm = !Number.isNaN(maxTerm) && maxTerm > 0;
		const availablePlazos = hasMaxTerm
			? this.plazos.filter(plazo => Number(plazo.val) <= maxTerm)
			: [...this.plazos];
		// Si el plazo autorizado es menor a todas las opciones se deja la más
		// pequeña para no quedarnos sin selector.
		this.visiblePlazos =
			availablePlazos.length > 0 ? availablePlazos : this.plazos.slice(0, 1);

		const selectedPlazo = this.resolveSelectedPlazo();
		this.plazos.forEach(plazo => {
			plazo.active = Number(plazo.val) === Number(selectedPlazo);
		});
		this.valInput = selectedPlazo;
		this.emitPlazo(Number(selectedPlazo));
	}

	/**
	 * Conserva el plazo recibido si sigue disponible; si no, cae al mayor de los
	 * plazos permitidos.
	 */
	private resolveSelectedPlazo(): Number {
		const isSelectedPlazoAvailable = this.visiblePlazos.some(
			plazo => Number(plazo.val) === Number(this.valInput)
		);
		if (isSelectedPlazoAvailable) {
			return this.valInput;
		}
		return this.visiblePlazos.reduce((biggestPlazo, plazo) =>
			Number(plazo.val) > Number(biggestPlazo.val) ? plazo : biggestPlazo
		).val;
	}

	private emitPlazo(plazo: number): void {
		if (this.lastEmittedPlazo === plazo) {
			return;
		}
		this.lastEmittedPlazo = plazo;
		// Se emite fuera del ciclo de detección de cambios en curso: al recortar
		// los plazos por el máximo autorizado el padre cambia el plazo que nos
		// enlaza, y hacerlo dentro del mismo ciclo dispara
		// ExpressionChangedAfterItHasBeenCheckedError.
		Promise.resolve().then(() => this.plazoChange.emit(plazo));
	}
}
